// src/components/NotificationBell.jsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { socket } from "../socket";
import api from "../api";
import { Link } from 'react-router-dom';

export default function NotificationBell({ token }) {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const rootRef = useRef(null);


  const loadUnread = useCallback(async () => {
    if (!token) { setUnread(0); return; }
    try {
      const r = await api.get("/notifications/unread-count");
      setUnread(r.data.unread || 0);
    } catch (err) {
      console.warn('unread-count err', err?.response?.data || err.message);
    }
  }, [token]);

  const loadNotifications = useCallback(async () => {
    if (!token) return;
    setLoadingList(true);
    try {
      const r = await api.get("/notifications?limit=50");
      setNotifications(r.data.notifications || []);
    } catch (err) {
      console.warn('notifications list err', err?.response?.data || err.message);
    } finally {
      setLoadingList(false);
    }
  }, [token]);

  // initial unread count
  useEffect(() => { loadUnread(); }, [loadUnread]);

  // 🔔 SOCKET LISTENER (NO CONNECT HERE)
  useEffect(() => {
    if (!socket) return;

    const handler = () => {
      loadUnread(); // refresh unread count
    };

    socket.on("notification", handler);

    return () => {
      socket.off("notification", handler);
    };
  }, [loadUnread]);

  // close dropdown on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  const markRead = async (nid) => {
    if (!token) return;
    try {
      await api.post(`/notifications/${nid}/read`);
      setNotifications(prev => prev.map(x => x.id === nid ? ({ ...x, is_read: true }) : x));
      setUnread(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.warn('mark read err', err?.response?.data || err.message);
    }
  };

  const markAll = async () => {
    if (!token) return;
    try {
      await api.post("/notifications/mark-all-read");
      setNotifications(prev => prev.map(x => ({ ...x, is_read: true })));
      setUnread(0);
    } catch (err) {
      console.warn('mark all err', err?.response?.data || err.message);
    }
  };

  const onToggle = async () => {
  setOpen(prev => {
    const willOpen = !prev;

    if (willOpen && token) {
      loadNotifications();

      api.post("/notifications/mark-all-read")
        .then(() => {
          setUnread(0);
          setNotifications(prev =>
            prev.map(n => ({ ...n, is_read: true }))
          );
        })
        .catch(() => {
          console.warn('auto mark-all-read failed');
        });
    }

    return willOpen;
  });
};

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
  onClick={onToggle}
  aria-label="Notifications"
  style={{
    background: '#0f1218',
    border: '1px solid #1f2937',
    color: '#e5e7eb',
    borderRadius: 10,
    padding: '6px 10px',
    cursor: 'pointer',
    fontSize: 18,
    display: 'flex',
    alignItems: 'center',
    gap: 6
  }}
>
        🔔
        {unread > 0 && (
          <span style={{
  marginLeft: 6,
  background: '#ef4444',
  color: 'white',
  borderRadius: 12,
  padding: '2px 7px',
  fontSize: 12,
  fontWeight: 600
}}>
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'fixed',
          right: 8,
          top: 56,
          width: 'min(360px, 92vw)',
          maxHeight: '70vh',
          overflow: 'auto',
          background: '#0f1218',
          color: '#d9e2ec',
          border: '1px solid #2a2f3a',
          borderRadius: 12,
          padding: 8,
          zIndex: 1000
        }}>
          {loadingList && <div style={{ padding: 12 }}>Loading…</div>}
          {!loadingList && notifications.length === 0 && (
            <div style={{ padding: 12, color: '#8aa0b4' }}>No notifications</div>
          )}
          {!loadingList && notifications.map(n => (
  <div
    key={n.id}
    style={{
      padding: '10px 12px',
      marginBottom: 6,
      borderRadius: 8,
      background: n.is_read ? '#0f1218' : '#111827',
      border: '1px solid #1f2937',
      fontSize: 14,
      lineHeight: 1.4
    }}
  >
    <strong>@{n.actor_user_id}</strong>{" "}
    <span style={{ color: '#9ca3af' }}>
      {n.type === 'support' && 'supported your post'}
      {n.type === 'comment' && 'commented on your post'}
      {n.type === 'repost' && 'reposted your post'}
      {n.type === 'follow' && 'started following you'}
    </span>
  </div>
))}
        </div>
      )}
    </div>
  );
}